import { type ChangePasswordInput, changePasswordSchema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { changePassword } from '../api';

function formatPasswordSuccessMessage(revokedSessions: number): string {
  if (revokedSessions === 0) return 'Mot de passe modifié.';
  if (revokedSessions === 1) {
    return 'Mot de passe modifié — 1 autre session a été déconnectée.';
  }
  return `Mot de passe modifié — ${revokedSessions} autres sessions ont été déconnectées.`;
}

export function PasswordChangeForm() {
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '' },
  });

  return (
    <section className="app-card overflow-hidden">
      <div className="border-b border-slate-900/10 bg-gradient-to-r from-indigo-50/90 via-white to-transparent px-8 py-7">
        <h3 className="text-[0.9rem] font-semibold uppercase tracking-[0.1em] text-indigo-900/92">
          Identifiants
        </h3>
        <p className="mt-2 text-[0.85rem] text-slate-600">
          Des secrets robustes protègent l’organisation — la nouvelle valeur doit respecter les
          règles de longueur affichées à la validation.
        </p>
      </div>
      <form
        className="space-y-6 p-11"
        onSubmit={handleSubmit(async (data) => {
          setError(null);
          setSuccess(null);
          try {
            const res = await changePassword(data);
            setSuccess(formatPasswordSuccessMessage(res.revokedSessions));
            reset();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Impossible de modifier le mot de passe');
          }
        })}
      >
        <div>
          <label htmlFor="currentPassword" className="app-label">
            Mot de passe actuel
          </label>
          <input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            className="app-input"
            {...register('currentPassword')}
          />
          {errors.currentPassword && (
            <p className="text-error mt-2">{errors.currentPassword.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="newPassword" className="app-label">
            Nouveau mot de passe
          </label>
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            className="app-input"
            {...register('newPassword')}
          />
          {errors.newPassword && <p className="text-error mt-2">{errors.newPassword.message}</p>}
        </div>

        {error && <p className="text-error">{error}</p>}
        {success && (
          <p className="text-success rounded-xl bg-emerald-50 px-5 py-[0.775rem] ring-1 ring-emerald-950/33">
            {success}
          </p>
        )}

        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Mise à jour…' : 'Mettre à jour le mot de passe'}
        </button>
      </form>
    </section>
  );
}
